import {
  Check,
  ChevronDown,
  MoreHorizontal,
  Pencil,
  Pin,
  PinOff,
  Save,
  Settings2,
  Share2,
  Trash2,
  User as UserIcon,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import type { SavedViewRead } from "@/api/types";

interface LayoutViewSelectorProps {
  views: SavedViewRead[];
  activeViewId: number | null;
  defaultViewId: number | null;
  /** Whether the current operator can manage shared views —
   *  controls visibility of "Set as default" and the share
   *  toggle on shared rows. Falls back to is_mine for
   *  per-row destructive actions. */
  canManageShared: boolean;
  onSelectView: (view: SavedViewRead | null) => void;
  onSaveAsNew: () => void;
  onManage: () => void;
  /** Inline rename — triggered via the per-row kebab. The host
   *  picks the UX (prompt dialog, inline edit, …); this component
   *  just forwards the click. */
  onRenameView: (view: SavedViewRead) => void;
  onDeleteView: (view: SavedViewRead) => void;
  onToggleShare: (view: SavedViewRead) => void;
  onToggleDefault: (view: SavedViewRead) => void;
}

/**
 * Compact dropdown for the Company-detail layout views. Shows the
 * active view's name + a per-row badge (📌 default / 🔗 shared /
 * 👤 private). Each row carries a kebab (⋯) sub-menu with
 * rename / share / set-default / delete so quick actions don't
 * require opening the full Manage dialog.
 */
export function LayoutViewSelector({
  views,
  activeViewId,
  defaultViewId,
  canManageShared,
  onSelectView,
  onSaveAsNew,
  onManage,
  onRenameView,
  onDeleteView,
  onToggleShare,
  onToggleDefault,
}: LayoutViewSelectorProps) {
  const activeView = views.find((v) => v.id === activeViewId) ?? null;
  const triggerLabel = activeView?.name ?? "Default";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex h-7 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-600 transition-colors hover:border-slate-300 hover:text-slate-900"
        >
          <span className="text-[10px] font-medium uppercase tracking-wider text-slate-400">
            Layout
          </span>
          <span className="font-medium text-slate-700">{triggerLabel}</span>
          <ChevronDown className="h-3 w-3 text-slate-400" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        {views.length === 0 ? (
          <div className="px-3 py-2 text-xs text-slate-400">
            No saved views yet.
          </div>
        ) : (
          views.map((view) => (
            <ViewRow
              key={view.id}
              view={view}
              isActive={view.id === activeViewId}
              isProjectDefault={view.id === defaultViewId}
              canManageShared={canManageShared}
              onSelect={onSelectView}
              onRename={onRenameView}
              onDelete={onDeleteView}
              onToggleShare={onToggleShare}
              onToggleDefault={onToggleDefault}
            />
          ))
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={onSaveAsNew}
          className="cursor-pointer gap-2"
        >
          <Save className="h-3.5 w-3.5 text-slate-500" />
          <span>Save current as new view</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={onManage}
          className="cursor-pointer gap-2"
        >
          <Settings2 className="h-3.5 w-3.5 text-slate-500" />
          <span>Manage views</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface ViewRowProps {
  view: SavedViewRead;
  isActive: boolean;
  isProjectDefault: boolean;
  canManageShared: boolean;
  onSelect: (view: SavedViewRead) => void;
  onRename: (view: SavedViewRead) => void;
  onDelete: (view: SavedViewRead) => void;
  onToggleShare: (view: SavedViewRead) => void;
  onToggleDefault: (view: SavedViewRead) => void;
}

/** One row in the dropdown list — clickable label + kebab menu
 *  for per-view actions. The kebab nests its own DropdownMenu
 *  inside the parent; the trigger stops propagation so opening
 *  the kebab doesn't fire the row's selection. */
function ViewRow({
  view,
  isActive,
  isProjectDefault,
  canManageShared,
  onSelect,
  onRename,
  onDelete,
  onToggleShare,
  onToggleDefault,
}: ViewRowProps) {
  // Rename / delete are gated on ownership; admins can also act
  // on shared views (canManageShared).
  const canMutateOwn = view.is_mine;
  const canMutateAny = canMutateOwn || canManageShared;
  // Share toggle only makes sense on the operator's own view —
  // admins manipulate share state via the Manage dialog. Set-as-
  // default is admin-only on shared views.
  const canToggleShare = canMutateOwn;
  const canToggleDefault = canManageShared && view.is_shared;

  return (
    <div
      className={cn(
        "group flex items-center gap-2 rounded-md px-2 py-1.5 text-sm",
        isActive ? "bg-indigo-50 text-indigo-700" : "text-slate-700",
      )}
    >
      <button
        type="button"
        onClick={() => onSelect(view)}
        className="flex min-w-0 flex-1 items-center gap-2 text-left"
      >
        <ViewBadge view={view} isProjectDefault={isProjectDefault} />
        <span className="flex-1 truncate">{view.name}</span>
        {isActive && (
          <Check className="h-3.5 w-3.5 shrink-0 text-indigo-500" />
        )}
      </button>
      {canMutateAny && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              onClick={(e) => e.stopPropagation()}
              aria-label={`Actions for ${view.name}`}
              className="rounded p-1 text-slate-400 opacity-0 transition-opacity hover:bg-slate-100 hover:text-slate-700 group-hover:opacity-100 data-[state=open]:opacity-100"
            >
              <MoreHorizontal className="h-3.5 w-3.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            {canMutateAny && (
              <DropdownMenuItem
                onSelect={() => onRename(view)}
                className="cursor-pointer gap-2"
              >
                <Pencil className="h-3.5 w-3.5 text-slate-500" />
                <span>Rename</span>
              </DropdownMenuItem>
            )}
            {canToggleShare && (
              <DropdownMenuItem
                onSelect={() => onToggleShare(view)}
                className="cursor-pointer gap-2"
              >
                <Share2 className="h-3.5 w-3.5 text-slate-500" />
                <span>
                  {view.is_shared ? "Make private" : "Share with team"}
                </span>
              </DropdownMenuItem>
            )}
            {canToggleDefault && (
              <DropdownMenuItem
                onSelect={() => onToggleDefault(view)}
                className="cursor-pointer gap-2"
              >
                {view.is_default ? (
                  <PinOff className="h-3.5 w-3.5 text-slate-500" />
                ) : (
                  <Pin className="h-3.5 w-3.5 text-slate-500" />
                )}
                <span>
                  {view.is_default ? "Unset as default" : "Set as default"}
                </span>
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => onDelete(view)}
              className="cursor-pointer gap-2 text-rose-600 focus:bg-rose-50 focus:text-rose-700"
            >
              <Trash2 className="h-3.5 w-3.5" />
              <span>Delete</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

function ViewBadge({
  view,
  isProjectDefault,
}: {
  view: SavedViewRead;
  isProjectDefault: boolean;
}) {
  if (isProjectDefault) {
    return <Pin className="h-3.5 w-3.5 shrink-0 text-amber-500" />;
  }
  if (view.is_shared) {
    return <Share2 className="h-3.5 w-3.5 shrink-0 text-indigo-500" />;
  }
  return <UserIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" />;
}
